import { Inject, Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { delay, mergeMap, take, tap } from 'rxjs/operators';
import { InitializationProgressComponent } from '../../components/initialization-progress/initialization-progress.component';
import { InitializationInstanceToken, InitializationInterface } from '../../interfaces/initialization.interface';
import moment from 'moment';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { YesNoModalDialogComponent } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.component';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { YesNoModalDialogModel } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.model';
import { TranslateService } from '@ngx-translate/core';
import { DeviceLockService } from '../../services/device/device-lock.service';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

@Injectable({
    providedIn: 'root',
})
export class AppInitializationGuard implements CanActivate {
    public constructor(
        @Inject(InitializationInstanceToken) private readonly initializationService: InitializationInterface,
        private readonly modalService: BsModalService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly deviceLockService: DeviceLockService,
        private readonly paceInsights: PaceInsightsService,
    ) {}

    public canActivate(): Observable<boolean> {
        if (this.initializationService.isInitialized) {
            return of(true);
        }

        const modalInstance = this.modalService.show(InitializationProgressComponent, {
            backdrop: 'static',
            class: 'modal-md modal-dialog-centered',
            initialState: {
                progress$: this.initializationService.initMessages$,
            },
        });

        const halfSecond = 500;
        const now = moment();
        const modalCloseTime = now.add(halfSecond, 'milliseconds');

        // delayWhen is used to make sure that the loading screen is displayed at least 500 milliseconds before hide is
        // being called.
        // Background: ngx-bootstrap modals have a bug where they will not hide if hide() is called before they are
        //             actually drawn on the screen. That means the modal would stay open indefinitely if the service
        //             took less then the time it takes to render the modal. Since the modal uses an animation to appear
        //             and that animation takes 500 milliseconds, this is the amount of time we are waiting before
        //             trying to attempt to close the modal.
        return (
            modalInstance.content?.rendered$.pipe(
                mergeMap(() => this.initializationService.checkInit()),
                delay(modalCloseTime.toDate()),
                tap((initSuccess) => {
                    if (initSuccess) {
                        modalInstance.hide();
                        this.triggerSyncAfterBoot();
                        this.checkIncognito();
                    }
                }),
            ) ?? of(false)
        );
    }

    private triggerSyncAfterBoot(): void {
        this.synchronizationService
            .receiveEverything(true)
            .pipe(take(1))
            .subscribe({
                error: (err) => {
                    this.log.error('AppInitializationGuard: triggerSyncAfterBoot: Background sync after boot failed', {
                        err,
                    });
                },
            });
    }

    private checkIncognito(): void {
        if (this.deviceLockService.deviceFingerprint.isPrivateBrowser) {
            this.paceInsights.trackEvent('IncognitoSessionStarted');
            this.modalService.show(YesNoModalDialogComponent, {
                backdrop: 'static',
                class: ModalClass.MODAL_MEDIUM_NO_FULLSCREEN,
                ignoreBackdropClick: true,
                initialState: {
                    model: new YesNoModalDialogModel(
                        this.translate.instant('INCOGNITO_WARNING_MODAL.Title'),
                        this.translate.instant('INCOGNITO_WARNING_MODAL.Message'),
                        this.translate.instant('INCOGNITO_WARNING_MODAL.Ok'),
                    ),
                },
                ariaLabelledBy: 'yes-no-modal-title',
            });
        }
    }
}
